import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Check, Copy, Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

import { FormInput, FormSelect, FormTextarea } from "@/components/forms/FormControls";
import SectionCard from "@/components/common/SectionCard";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { AI_LISTING_TONE_OPTIONS } from "@/constant/deals-form.constant";
import { usePostAiListingDescription } from "@/hooks/useAi";
import { aiListingSchema } from "@/schemas/ai";

interface AiTabDeal {
  id: string;
  address?: string;
  notes?: string;
  phase?: string;
}

interface AiTabProps {
  deal: AiTabDeal;
}

/** `""` means the agent cleared the field — send nothing rather than a zero. */
const toNumber = (value: string | number) => (value === "" ? undefined : Number(value));

export default function AiTab({ deal }: AiTabProps) {
  const [description, setDescription] = useState("");
  const [copied, setCopied] = useState(false);

  const { mutate: generateListing, isPending: generatingListing } = usePostAiListingDescription();

  const form = useForm({
    resolver: zodResolver(aiListingSchema),
    defaultValues: {
      address: deal.address ?? "",
      beds: "",
      baths: "",
      sqft: "",
      features: deal.notes ?? "",
      tone: AI_LISTING_TONE_OPTIONS[0].value,
    },
  });

  const submitListing = form.handleSubmit((values) => {
    generateListing(
      {
        address: values.address,
        beds: toNumber(values.beds),
        baths: toNumber(values.baths),
        sqft: toNumber(values.sqft),
        features: values.features,
        tone: values.tone,
      },
      { onSuccess: (res) => setDescription(res.data?.description ?? "") }
    );
  });

  const copyDescription = async () => {
    await navigator.clipboard.writeText(description);
    setCopied(true);
    toast.success("Description copied");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="flex flex-col gap-5" data-testid="ai-tab">
      <SectionCard
        title={
          <span className="flex items-center gap-2">
            <Wand2 className="size-4 text-dash-greenDeep" /> Listing description
          </span>
        }
        description="An MLS-ready draft in 130–180 words. Yours to edit before it goes anywhere."
      >
        <Form {...form}>
          <form onSubmit={submitListing} className="flex flex-col gap-5" noValidate>
            <FormInput name="address" label="Property address" data-testid="ai-listing-address" required />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormInput name="beds" label="Beds" type="number" min="0" placeholder="3" data-testid="ai-listing-beds" />
              <FormInput name="baths" label="Baths" type="number" min="0" step="0.5" placeholder="2" data-testid="ai-listing-baths" />
              <FormInput name="sqft" label="Square feet" type="number" min="0" placeholder="1800" data-testid="ai-listing-sqft" />
            </div>
            <FormSelect name="tone" label="Tone" options={AI_LISTING_TONE_OPTIONS} data-testid="ai-listing-tone" required />
            <FormTextarea
              name="features"
              label="Features to highlight"
              rows={4}
              placeholder="Renovated kitchen, south-facing garden, walk to the station…"
              className="resize-none"
              data-testid="ai-listing-features"
            />
            <div className="flex justify-end">
              <Button type="submit" variant="dashboardPrimary" disabled={generatingListing} data-testid="ai-listing-btn">
                {generatingListing ? <Loader2 data-icon="inline-start" className="animate-spin" /> : <Sparkles data-icon="inline-start" />}
                {generatingListing ? "Writing…" : description ? "Rewrite" : "Write description"}
              </Button>
            </div>
          </form>
        </Form>

        {description && (
          <div className="mt-5 rounded-xl border border-dash-border bg-dash-searchBg p-4 sm:p-5" data-testid="ai-listing-output">
            <p className="whitespace-pre-wrap text-sm leading-6 text-dash-ink">{description}</p>
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-xs text-dash-faint">{description.trim().split(/\s+/).length} words</span>
              <Button type="button" variant="dashboardOutline" size="sm" onClick={copyDescription} data-testid="ai-listing-copy">
                {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
